/**
 * lists.js — Firestore for the lists staff manage in the app (their rules: logic/lists.js).
 *
 *   opsLists/{autoId}     a custom list   { custom:true, label, icon, color, kind:'buy'|'task',
 *                                           qty, order, createdBy, createdAt, deleted? }
 *   opsLists/{builtinId}  a restyled built-in list { label?, icon?, color?, order? }
 *   opsLists/_routes      { routes: { "<fromList>::<name>": listId },   — item moves
 *                           hidden: { "<listId>::<name>": Timestamp } } — names removed from a
 *                           list's suggestions at that time (logic/catalogs.js)
 *   opsLists/_deleted     tombstones of deleted checklist-history rows (data/checklists.js)
 *
 * The whole collection is a few dozen docs, so one shared, lingering listener (liveFeed)
 * covers every screen for the cost of reading it once.
 */
import {
  collection, doc, query, limit, onSnapshot,
  addDoc, setDoc, deleteDoc, writeBatch, serverTimestamp, deleteField,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { shareFeed } from './liveFeed';
import { rowTracker } from './rows';
import { debugLog, snapInfo } from './debug';
import { archivePatch } from './items';
import { ITEMS_COLLECTION, LISTS_COLLECTION, ROUTES_DOC_ID } from '../config/constants';

const listDoc = id => doc(db, LISTS_COLLECTION, id);
const itemDoc = id => doc(db, ITEMS_COLLECTION, id);

/** Live list docs (custom lists, restyled built-ins, the routes doc). */
export function subscribeLists(onData, onError) {
  return shareFeed(LISTS_COLLECTION, (emit, fail) => {
    const rowsOf = rowTracker();
    return onSnapshot(query(collection(db, LISTS_COLLECTION), limit(300)), snap => {
      debugLog('snapshot', LISTS_COLLECTION, snapInfo(snap));
      emit(rowsOf(snap));
    }, fail);
  }, onData, onError);
}

/** Create a list; resolves to its doc ref (the id is the new category value). */
export function createList({ label, icon, color, kind = 'buy', qty = true, order = 0, by = '' }) {
  return addDoc(collection(db, LISTS_COLLECTION), {
    custom: true, label: String(label || '').trim(), icon, color,
    kind: kind === 'task' ? 'task' : 'buy', qty: !!qty, order,
    createdBy: by, createdAt: serverTimestamp(),
  });
}

/** Rename / restyle a list (custom, or a built-in's override doc — created on first save). */
export function saveList(id, patch) {
  return setDoc(listDoc(id), patch, { merge: true });
}

/** Put a built-in list back to its default name / icon / colour / position. */
export function resetList(id) {
  return deleteDoc(listDoc(id));
}

/** Swap two lists' positions (one batch; works for built-ins with no doc yet). */
export function swapListOrder(a, b) {
  const batch = writeBatch(db);
  batch.set(listDoc(a.id), { order: b.order }, { merge: true });
  batch.set(listDoc(b.id), { order: a.order }, { merge: true });
  return batch.commit();
}

// Write entries ({ key: value | undefined = remove }) into one map of the routes doc
// (`routes` or `hidden`).
function setRouteDocMap(batch, field, entries) {
  const map = {};
  Object.entries(entries).forEach(([k, v]) => { map[k] = v === undefined ? deleteField() : v; });
  if (Object.keys(map).length) batch.set(listDoc(ROUTES_DOC_ID), { [field]: map }, { merge: true });
}
const setRoutes = (batch, entries) => setRouteDocMap(batch, 'routes', entries);

/**
 * Delete a custom list. Its items still on the open list move to History in the same
 * batch, and item moves into / out of it (`routes`: the entries to drop) are cleared —
 * its names go back to suggesting where they were noted. A list nobody ever noted
 * anything in is removed outright; one with history is only flagged `deleted`, so
 * History keeps showing its name.
 */
export function deleteList({ id, hard = false, archiveIds = [], routes = {}, by = '' }) {
  const batch = writeBatch(db);
  if (hard) batch.delete(listDoc(id));
  else batch.set(listDoc(id), { deleted: true, deletedBy: by, deletedAt: serverTimestamp() }, { merge: true });
  archiveIds.forEach(i => batch.update(itemDoc(i), archivePatch(true, by)));
  setRoutes(batch, Object.fromEntries(Object.keys(routes).map(k => [k, undefined])));
  return batch.commit();
}
/** Undo for deleteList — `data` is the list doc as it was (needed after a hard delete). */
export function restoreList({ id, hard = false, data = {}, archiveIds = [], routes = {} }) {
  const batch = writeBatch(db);
  if (hard) batch.set(listDoc(id), data);
  else batch.set(listDoc(id), { deleted: false, deletedBy: deleteField(), deletedAt: deleteField() }, { merge: true });
  archiveIds.forEach(i => batch.update(itemDoc(i), archivePatch(false)));
  setRoutes(batch, routes);
  return batch.commit();
}

/**
 * Move item names between lists in one batch: route entries (`next` undefined = remove
 * the entry) + the open-list items that move with them ({ id, category }). Undo = call
 * again with each route's `prev` and the items' old categories.
 */
export function moveNames({ routes = [], items = [] }) {
  const batch = writeBatch(db);
  setRoutes(batch, Object.fromEntries(routes.map(r => [r.key, r.next])));
  items.forEach(({ id, category }) => batch.update(itemDoc(id), { category }));
  return batch.commit();
}

/**
 * Remove names from a list's suggestions — `keys` (logic/lists.js planHide) are stamped
 * with the server's time: notes made before it stop counting, so noting a name again
 * brings it back. Undo = restoreHiddenNames with each entry's `prev`.
 */
export function hideNames(keys = []) {
  const batch = writeBatch(db);
  setRouteDocMap(batch, 'hidden', Object.fromEntries(keys.map(k => [k, serverTimestamp()])));
  return batch.commit();
}
/** Put hidden-name entries back as they were: [{ key, prev }] (`prev` undefined = none). */
export function restoreHiddenNames(entries = []) {
  const batch = writeBatch(db);
  setRouteDocMap(batch, 'hidden', Object.fromEntries(entries.map(e => [e.key, e.prev])));
  return batch.commit();
}
